/**
 * Build Agent API client — typed fetch wrappers for the Build Agent endpoints.
 */

import type { GraphDefinition } from "./graphs";

export interface BuildAgentMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: Array<{ name: string; input: Record<string, unknown>; output?: unknown }>;
  timestamp: string;
}

export interface BuildAgentConversation {
  id: string;
  companyId: string;
  graphId: string | null;
  title: string | null;
  messages: BuildAgentMessage[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface GraphOperation {
  type:
    | "add_node"
    | "remove_node"
    | "update_node"
    | "add_edge"
    | "remove_edge"
    | "update_edge";
  nodeId?: string;
  edgeId?: string;
  node?: Record<string, unknown>;
  edge?: Record<string, unknown>;
  updates?: Record<string, unknown>;
}

export interface GraphDiffProposal {
  proposalId: string;
  description: string;
  operations: GraphOperation[];
  summary: {
    addNodes: number;
    removeNodes: number;
    updateNodes: number;
    addEdges: number;
    removeEdges: number;
    updateEdges: number;
    nodeCountDelta: number;
    edgeCountDelta: number;
  };
}

/** Phase 5 — file-level change proposal */
export interface FileDiffProposal {
  proposalId: string;
  type: "file_diff";
  filePath: string;
  description: string;
  operationType: "create_file" | "modify_file" | "delete_file";
  fileOldContent?: string;
  fileContent?: string;
}

export interface ChatStreamBody {
  message: string;
  conversationId?: string;
  graphId: string;
  modelId?: string;
  mode?: "graph" | "workflow" | "code";
}

export type BuildAgentSSEEvent =
  | { type: "message"; content: string }
  | { type: "tool_call"; name: string; input: Record<string, unknown> }
  | { type: "graph_diff"; proposalId: string; description: string; operations: GraphOperation[]; summary: GraphDiffProposal["summary"] }
  | { type: "file_diff"; proposalId: string; filePath: string; description: string; operationType: "create_file" | "modify_file" | "delete_file"; fileOldContent?: string; fileContent?: string }
  | { type: "error"; message: string }
  | { type: "done"; conversationId: string };

/**
 * Open an SSE stream to the Build Agent chat endpoint.
 * Returns an AbortController so the caller can cancel.
 */
export function chatStream(
  companyId: string,
  body: ChatStreamBody,
  onEvent: (event: BuildAgentSSEEvent) => void,
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(`/api/companies/${companyId}/build-agent/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "Unknown error");
        onEvent({ type: "error", message: text });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        let eventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ") && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              onEvent({ type: eventType, ...data } as BuildAgentSSEEvent);
            } catch {
              // Skip malformed JSON
            }
            eventType = "";
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      onEvent({ type: "error", message: (err as Error).message });
    }
  })();

  return controller;
}

export async function getConversations(companyId: string): Promise<BuildAgentConversation[]> {
  const res = await fetch(`/api/companies/${companyId}/build-agent/conversations`);
  if (!res.ok) throw new Error("Failed to fetch conversations");
  return res.json();
}

export async function getConversation(
  companyId: string,
  id: string,
): Promise<BuildAgentConversation> {
  const res = await fetch(`/api/companies/${companyId}/build-agent/conversations/${id}`);
  if (!res.ok) throw new Error("Failed to fetch conversation");
  return res.json();
}

export async function applyProposal(
  companyId: string,
  conversationId: string,
  proposalId: string,
): Promise<{ graphDefinition?: GraphDefinition; rules?: unknown[] }> {
  const res = await fetch(
    `/api/companies/${companyId}/build-agent/conversations/${conversationId}/apply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ proposalId }),
    },
  );
  if (!res.ok) throw new Error("Failed to apply proposal");
  return res.json();
}

export const buildAgentApi = {
  chatStream,
  getConversations,
  getConversation,
  applyProposal,
};
